import { useEffect } from 'react';

/**
 * Swap the browser tab favicon for the lifetime of the mounting component.
 * On unmount the previous icon is restored, so navigating from Mirror back
 * to Mission Control returns to the default Seertech favicon automatically.
 *
 * Usage:
 *   useFavicon('/mirror_favicon.png');
 *
 * Notes:
 *  • Targets every `<link rel*="icon">` element so Chrome, Safari and Firefox
 *    all pick up the change (they each prefer different sizes / mime types).
 *  • If no icon links are present (very old SSR shell) this is a no-op.
 *  • Pass `null` / `undefined` to skip — handy for conditional usage from
 *    layouts where some routes want the default.
 */
export default function useFavicon(href) {
  useEffect(() => {
    if (!href) return undefined;
    const links = Array.from(document.querySelectorAll('link[rel~="icon"]'));
    if (!links.length) return undefined;

    const previous = links.map((l) => ({ el: l, href: l.getAttribute('href') }));
    links.forEach((l) => l.setAttribute('href', href));

    return () => {
      previous.forEach(({ el, href: prev }) => {
        if (prev == null) el.removeAttribute('href');
        else el.setAttribute('href', prev);
      });
    };
  }, [href]);
}
